import { ApiService } from '@sparklayer-io/spark-dash-core/types/api';
import type {
  AvailableTeam,
  ConfirmedPasswordStatusApiResponse,
  ConfirmPasswordApiRequest,
  ConfirmTwoFactorApiRequest,
  RecoveryCodesApiResponse,
  TwoFactorQrCodeApiResponse,
  UpdateAccountApiRequest,
  UpdatePasswordApiRequest,
} from '../types';
import { TeamApi, type TeamApiContract } from './team-api';

export interface AccountApiContract {
  updatePassword(data: UpdatePasswordApiRequest): Promise<void>;
  team(teamId: number | undefined): TeamApiContract;
  enableTwoFactor(): Promise<void>;
  twoFactorQrCode(): Promise<TwoFactorQrCodeApiResponse>;
  confirmTwoFactor(data: ConfirmTwoFactorApiRequest): Promise<void>;
  /** Only for rotating a factor - see `useResetTwoFactor`. There is no "turn 2FA off" action. */
  disableTwoFactor(): Promise<void>;
  recoveryCodes(): Promise<RecoveryCodesApiResponse>;
  regenerateRecoveryCodes(): Promise<void>;
  confirmedPasswordStatus(): Promise<ConfirmedPasswordStatusApiResponse>;
  confirmPassword(data: ConfirmPasswordApiRequest): Promise<void>;
  getAvailableTeams(): Promise<AvailableTeam[]>;
  updateAccount(data: UpdateAccountApiRequest): Promise<void>;
}

declare module '@sparklayer-io/spark-dash-core/context/page-context' {
  interface Apis {
    account: AccountApiContract;
  }
}

export class AccountApi extends ApiService implements AccountApiContract {
  public async updatePassword(data: UpdatePasswordApiRequest): Promise<void> {
    await this.api.fetch({
      method: 'PUT',
      path: '/user/password',
      payload: data,
    });
  }

  public team(teamId: number | undefined): TeamApiContract {
    if (teamId) {
      return new TeamApi(this.api, teamId);
    } else {
      throw new Error('team_id_undefined');
    }
  }

  /**
   * Issues a secret and recovery codes only when the user has none - so it is a no-op for an
   * already-enrolled user, and rotating a factor means disabling it first (`useResetTwoFactor`).
   * Fortify's `force` flag would overwrite in place, but it leaves `two_factor_confirmed_at`
   * set, which arms a challenge against a secret nobody has scanned yet. Do not add it.
   * See `Laravel\Fortify\Actions\EnableTwoFactorAuthentication`.
   */
  public async enableTwoFactor(): Promise<void> {
    await this.api.fetch({
      method: 'POST',
      path: '/user/two-factor-authentication',
    });
  }

  public async twoFactorQrCode(): Promise<TwoFactorQrCodeApiResponse> {
    return await this.api.fetch({
      method: 'GET',
      path: '/user/two-factor-qr-code',
    });
  }

  public async confirmTwoFactor(data: ConfirmTwoFactorApiRequest): Promise<void> {
    await this.api.fetch({
      method: 'POST',
      path: '/user/confirmed-two-factor-authentication',
      payload: data,
    });
  }

  /** Clears the secret, the recovery codes and the confirmation timestamp. */
  public async disableTwoFactor(): Promise<void> {
    await this.api.fetch({
      method: 'DELETE',
      path: '/user/two-factor-authentication',
    });
  }

  public async recoveryCodes(): Promise<RecoveryCodesApiResponse> {
    return await this.api.fetch({
      method: 'GET',
      path: '/user/two-factor-recovery-codes',
    });
  }

  public async regenerateRecoveryCodes(): Promise<void> {
    await this.api.fetch({
      method: 'POST',
      path: '/user/two-factor-recovery-codes',
    });
  }

  public async confirmedPasswordStatus(): Promise<ConfirmedPasswordStatusApiResponse> {
    return await this.api.fetch({
      method: 'GET',
      path: '/user/confirmed-password-status',
    });
  }

  public async confirmPassword(data: ConfirmPasswordApiRequest): Promise<void> {
    await this.api.fetch({
      method: 'POST',
      path: '/user/confirm-password',
      payload: data,
    });
  }

  public async getAvailableTeams(): Promise<AvailableTeam[]> {
    return await this.api.fetch({
      method: 'GET',
      path: '/available-teams',
    });
  }

  public updateAccount(data: UpdateAccountApiRequest): Promise<void> {
    return this.api.fetch({
      method: 'PUT',
      path: '/user/profile-information',
      payload: data,
    });
  }
}
